import { useEffect, useRef } from 'react';
import { usersApi } from '../../api/users';
import { recommendationRequestId, PAGE_SIZE } from './memberModel';
let sequence = 0;
export default function useMemberTracking(entry, user, items, recommended) {
  const nodes = useRef(new Map()),
    latest = useRef(null);
  if (!entry.tracking)
    entry.tracking = {
      nonce: `${Date.now().toString(36)}:${(++sequence).toString(36)}`,
      sent: new Set(),
      requests: new Map(),
    };
  const state = entry.tracking;
  const eventFor = (item, index) => {
    if (!state.requests.has(item.id)) {
      const page = Math.floor(index / PAGE_SIZE);
      state.requests.set(
        item.id,
        recommendationRequestId(
          user.id,
          state.nonce,
          items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
          page,
        ),
      );
    }
    return {
      targetUserId: item.id,
      resultPosition: index + 1,
      surface: 'REVIEWER_FINDER',
      section: 'RECOMMENDED_USERS',
      algorithmVersion: 'taste-social-v2',
      reason: item.recommendationReason || 'ACTIVE_REVIEWER',
      requestId: state.requests.get(item.id),
    };
  };
  if (recommended) items.forEach(eventFor);
  latest.current = { items, recommended, eventFor };
  const send = (events) => {
    if (!entry.current() || !events.length) return;
    for (let i = 0; i < events.length; i += 40)
      void usersApi
        .recordMemberEvents(events.slice(i, i + 40), {
          isSessionCurrent: entry.current,
          suppressGlobalError: true,
        })
        .catch(() => {});
  };
  useEffect(() => {
    if (!recommended || typeof IntersectionObserver === 'undefined') return;
    let active = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!active || !entry.current() || document.hidden || !latest.current.recommended) return;
        const batch = [];
        for (const visible of entries) {
          const id = Number(visible.target.dataset.memberId),
            index = latest.current.items.findIndex((x) => Number(x.id) === id);
          if (
            index < 0 ||
            state.sent.has(id) ||
            !visible.isIntersecting ||
            visible.intersectionRatio < 0.5
          )
            continue;
          state.sent.add(id);
          batch.push({
            ...latest.current.eventFor(latest.current.items[index], index),
            eventType: 'RECOMMENDATION_IMPRESSION',
          });
        }
        send(batch);
      },
      { threshold: [0, 0.5, 1] },
    );
    nodes.current.forEach((n) => observer.observe(n));
    const resume = () => {
      if (!document.hidden)
        nodes.current.forEach((n) => {
          observer.unobserve(n);
          observer.observe(n);
        });
    };
    document.addEventListener('visibilitychange', resume);
    return () => {
      active = false;
      observer.disconnect();
      document.removeEventListener('visibilitychange', resume);
    };
    // Reconnect when the row set changes; sent IDs/request IDs survive search and history return.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, recommended, items]);
  return {
    rowRef: (id, node) => {
      if (node) nodes.current.set(id, node);
      else nodes.current.delete(id);
    },
    click: (item, index, follow = false) => {
      if (recommended)
        send([
          {
            ...eventFor(item, index),
            eventType: 'RECOMMENDATION_CLICK',
            context: follow ? 'reviewer_finder_follow' : 'reviewer_finder',
          },
        ]);
    },
  };
}
